import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import './ReactionPopover.css';

// The 6 quick-set emojis pinned in the design spec (design/desktop-screens.jsx
// line ~437). Order is intentional and matches the popover mock.
export const QUICK_REACTIONS = ['🔥', '❄', '😂', '🎉', '🌊', '✨'];
// "More" second set — surfaced when the user taps the + button. Same length
// so the popover doesn't reflow. A full emoji picker is v2 territory; this
// gets us a working + button without building one.
export const EXTENDED_REACTIONS = ['❤', '👍', '👎', '🤔', '😎', '🙏'];

/**
 * ReactionPopover — the glass dark pill floating above a Bubble.
 *
 * Props:
 *   onSelect:  (emoji) => void         — fires when a quick-emoji is tapped
 *   onReply:   () => void              — fires when the reply icon is tapped (stub)
 *   onMore:    () => void              — fires when the plus icon is tapped (stub
 *                                        — full picker is v2; renders the icon
 *                                        regardless per spec)
 *   onDismiss: () => void              — fires on outside click or Escape
 *
 * Dismiss behavior:
 *   - Outside click: pointerdown on the document outside the popover
 *   - Escape key: any keydown with key === 'Escape'
 *   - Selection: the caller is expected to call onDismiss after the toggle —
 *     but as a safety net we also call onDismiss inside the click handlers,
 *     because the spec says "Dismisses on … selection".
 *
 * Positioning:
 *   The popover renders absolutely positioned at `top: -46px; left: 44px` —
 *   matching the desktop mock — and the consumer is responsible for setting
 *   `position: relative` on the wrapper around the bubble. That keeps us out
 *   of portal territory for a v1.
 */
function ReactionPopover({
  onSelect,
  onReply,
  onMore,
  onBlock,
  onDismiss,
  anchorRef,
  variant = 'other',
}) {
  const popRef = useRef(null);
  // When the user taps +, we swap the visible reaction set rather than
  // dismissing or opening a separate picker. Local state — resets on close.
  const [extended, setExtended] = useState(false);
  const reactions = extended ? EXTENDED_REACTIONS : QUICK_REACTIONS;

  // Dismiss on outside click. We use pointerdown so we win the race against
  // any click handlers inside the popover (those re-open / reselect).
  useEffect(() => {
    const onDocPointerDown = (e) => {
      const pop = popRef.current;
      const anchor = anchorRef && anchorRef.current;
      if (!pop) return;
      if (pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      if (typeof onDismiss === 'function') onDismiss();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [onDismiss, anchorRef]);

  // Dismiss on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && typeof onDismiss === 'function') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const handleSelect = (emoji) => {
    if (typeof onSelect === 'function') onSelect(emoji);
    if (typeof onDismiss === 'function') onDismiss();
  };

  const handleReply = () => {
    if (typeof onReply === 'function') onReply();
    if (typeof onDismiss === 'function') onDismiss();
  };

  const handleMore = () => {
    // Toggle to the secondary emoji set so the + button has real behavior
    // without us building a full emoji picker. The popover stays open so the
    // user can pick from the new set immediately.
    setExtended((v) => !v);
    if (typeof onMore === 'function') onMore();
  };

  const handleBlock = () => {
    if (typeof onBlock === 'function') onBlock();
    if (typeof onDismiss === 'function') onDismiss();
  };

  return (
    <div
      ref={popRef}
      className="glacier-react-popover"
      data-side={variant}
      role="menu"
      aria-label="React to message"
      data-testid="reaction-popover"
    >
      {reactions.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className="glacier-react-popover__btn glacier-react-popover__emoji"
          role="menuitem"
          aria-label={`React with ${emoji}`}
          onClick={() => handleSelect(emoji)}
        >
          {emoji}
        </button>
      ))}
      <span
        className="glacier-react-popover__divider"
        aria-hidden="true"
      />
      <button
        type="button"
        className="glacier-react-popover__btn glacier-react-popover__icon"
        role="menuitem"
        aria-label="Reply"
        onClick={handleReply}
      >
        <Icon name="reply" size={16} aria-hidden />
      </button>
      <button
        type="button"
        className="glacier-react-popover__btn glacier-react-popover__icon"
        role="menuitem"
        aria-label="More reactions"
        aria-pressed={extended || undefined}
        onClick={handleMore}
      >
        <Icon name="plus" size={16} aria-hidden />
      </button>
      {typeof onBlock === 'function' && (
        <button
          type="button"
          className="glacier-react-popover__btn glacier-react-popover__icon glacier-react-popover__block"
          role="menuitem"
          aria-label="Block this user"
          onClick={handleBlock}
        >
          <Icon name="logout" size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}

export default ReactionPopover;
